import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import Layout from './Layout';
import { useAuth, usePermissions } from '../lib/auth-context';
import type { Permission } from '../lib/permissions';

/**
 * Marco común para todas las páginas de administración. Envuelve el `Layout`
 * del campus (sidebar + barra superior + perfil).
 *
 * Gate (F3 RBAC): por PERMISO, no por `role === 'admin'`. Cada ruta admin pasa
 * el permiso que la gobierna vía `requiredPermission` (espejo del
 * `require_permission` del backend). Si no se pasa, el gate cae a "cualquier
 * permiso de admin" (entrar al panel). El gate REAL es server-side; este solo
 * evita pintar una página vacía/403 a quien no debe verla y redirige a su home.
 *
 * La navegación entre sub-secciones del admin vive en el SIDEBAR izquierdo,
 * como submenú anidado bajo "Panel admin" (ver AppShell + appshell-nav.ts),
 * filtrado también por permiso.
 *
 * Las páginas admin renderizan su contenido como children (con su propio
 * contenedor `mx-auto max-w-*`); NO deben pintar su propio `min-h-screen`/fondo.
 */
export default function AdminLayout({
  children,
  requiredPermission,
}: {
  children: ReactNode;
  /** Permiso que gobierna esta página admin. Si se omite, basta con tener
   *  cualquier permiso de admin (poder entrar al panel). */
  requiredPermission?: Permission;
}): JSX.Element {
  const { state } = useAuth();
  const { permissions, can } = usePermissions();

  if (state.status === 'loading') {
    return (
      <Layout hideBackButton>
        <div className="flex min-h-[60vh] items-center justify-center">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-ink/15 border-t-brand-violet" />
        </div>
      </Layout>
    );
  }
  if (state.status === 'unauthenticated') return <Navigate to="/login" replace />;

  const allowed = requiredPermission
    ? can(requiredPermission)
    : permissions.some((p) => p.startsWith('admin.'));
  if (!allowed) return <Navigate to="/dashboard" replace />;

  return <Layout hideBackButton>{children}</Layout>;
}
